// Presence at the kraal, as the cattle list and the animal page say it (plan
// smartkraal-owner-experience B1). GET /cattle carries `last_seen` (the last
// Kampala day the kraal read the animal's tag, YYYY-MM-DD) and `seen_days_ago`
// (counted in Kampala days by the server); the chip vocabulary is the same on
// the wall tile, the list row and the animal page:
//   "Seen today" / "Seen yesterday" / "Seen Tue"   read within the last 7 days
//   "Not seen 12 d"                                read before, not in 7 days
//   "Never at the kraal"                           a bound tag the kraal never read
//   "No tag"                                       no live SmartKraal binding
// Unbound animals are never "missing": the kraal cannot see them (decision 5).

// Kampala time, in one place (the SmartKraal pages import these through
// SmartKraalWeighings.jsx, which re-exports them).
const WEEKDAY = new Intl.DateTimeFormat('en-GB', { weekday: 'short' })
const DAY_SHORT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
const EAT_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Kampala', hour: '2-digit', minute: '2-digit', hour12: false })
const EAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Africa/Kampala', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false,
})
const KAMPALA_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Kampala', year: 'numeric', month: '2-digit', day: '2-digit' })

/** "28 Oct 2026, 06:31 EAT" for a UTC instant (every SmartKraal timestamp is UTC). */
export const formatEAT = (iso) => (iso ? `${EAT.format(new Date(iso))} EAT` : '—')

/** Today's calendar date in Kampala as YYYY-MM-DD; never slice toISOString() for this. */
export const kampalaToday = () => KAMPALA_DAY.format(new Date())

/** A calendar date ('YYYY-MM-DD') as a local Date at midnight; null when malformed. */
export function calendarDate(isoDate) {
  const [y, m, d] = String(isoDate || '').slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

export function weekdayOf(isoDate) {
  const d = calendarDate(isoDate)
  return d ? WEEKDAY.format(d) : ''
}

/** "Tue 28 Oct 2026" (a Kampala calendar day; no time zone shift, no comma). */
export function formatDay(isoDate, { weekday = true } = {}) {
  const d = calendarDate(isoDate)
  if (!d) return ''
  return weekday ? `${WEEKDAY.format(d)} ${DAY_SHORT.format(d)}` : DAY_SHORT.format(d)
}

/** "06:31" East Africa Time for a UTC instant. */
export function formatEATTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : EAT_TIME.format(d)
}

/**
 * The presence chip for a cattle-list row / wall tile: `{ key, label, title, cls }`.
 * `key` is one of seen | stale | never | notag.
 */
export function presenceChip(animal) {
  if (!animal) return null
  if (!animal.smartkraal_p_idtag) {
    return { key: 'notag', label: 'No tag', cls: 'bg-earth-100 text-earth-500',
             title: 'No SmartKraal tag is bound: the kraal cannot see this animal' }
  }
  const days = animal.seen_days_ago
  if (animal.last_seen == null || days == null) {
    return { key: 'never', label: 'Never at the kraal', cls: 'bg-amber-50 text-amber-800',
             title: 'The kraal has not read this tag yet' }
  }
  const title = `Last read by the kraal on ${formatDay(animal.last_seen)}`
  const seen = 'bg-primary-100 text-primary-800'
  if (days <= 0) return { key: 'seen', label: 'Seen today', title, cls: seen }
  if (days === 1) return { key: 'seen', label: 'Seen yesterday', title, cls: seen }
  if (days < 7) return { key: 'seen', label: `Seen ${weekdayOf(animal.last_seen)}`, title, cls: seen }
  return { key: 'stale', label: `Not seen ${days} d`, title, cls: 'bg-red-50 text-red-700' }
}

// The kraal feed's wording (plan B2), shared by the feed page and the dashboard card.
export const EXIT_LABEL = { death: 'Died', sale: 'Sold', lost: 'Lost' }

/** A count with thousands separators; '' for null. */
export const fmtN = (n) => (n == null ? '' : Number(n).toLocaleString())

/** "142 of your 187 seen" (a day scoped to the reader) or "300 of 1,497 seen" (the ranch). */
export function feedHeadline(day) {
  if (day.mine) return `${fmtN(day.mine.seen)} of your ${fmtN(day.mine.animals)} seen`
  return `${fmtN(day.seen)} of ${fmtN(day.herd)} seen`
}

/** "76 %" of seen over herd; '' without a herd. */
export function pctText(seen, herd) {
  return herd ? `${Math.round((100 * seen) / herd)} %` : ''
}

/** A weight from the API (a Decimal string or a number) without trailing zeros: "305.5", "240". */
export function formatKg(v) {
  const n = Number(v)
  if (v == null || v === '' || !Number.isFinite(n)) return v == null ? '' : String(v)
  return n % 1 === 0 ? String(n) : String(Math.round(n * 10) / 10)
}

/**
 * A weight change as the tile shows it: `{ dir: 'up'|'down'|'flat', text: '+12 kg' }`,
 * or null when there is no previous weighing to compare with.
 */
export function weightDelta(kg) {
  if (kg == null || kg === '' || Number.isNaN(Number(kg))) return null
  const n = Math.round(Number(kg) * 10) / 10
  if (n === 0) return { dir: 'flat', text: '±0 kg' }
  const text = `${n > 0 ? '+' : '−'}${Math.abs(n) % 1 === 0 ? Math.abs(n) : Math.abs(n).toFixed(1)} kg`
  return { dir: n > 0 ? 'up' : 'down', text }
}
